import React, { useState, useEffect } from 'react';
import { useRag } from '../../lib/rag/useRag';
import { QueryType } from '../../lib/rag/essentialExports';

/**
 * Debug component for the RAG system
 * This component displays the current state of the RAG system and allows testing
 */
export default function RagDebugger() {
  const { 
    loading, 
    initialized, 
    error, 
    query, 
    search, 
    searchResults, 
    result,
    debugInfo,
    initialize
  } = useRag();
  
  const [testQuery, setTestQuery] = useState('');
  const [queryType, setQueryType] = useState(QueryType.GENERAL);
  const [searchTerm, setSearchTerm] = useState('');
  const [debug, setDebug] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [showRawResponse, setShowRawResponse] = useState(false);
  
  // Update debug info periodically
  useEffect(() => {
    const updateDebug = () => {
      setDebug(debugInfo());
    };
    
    updateDebug();
    const interval = setInterval(updateDebug, 2000);
    
    return () => clearInterval(interval);
  }, [debugInfo]);
  
  // Handle test query
  const handleTestQuery = async (e) => {
    e.preventDefault();
    if (!testQuery.trim()) return;
    
    try {
      const result = await query(testQuery, queryType);
      setTestResult(result);
    } catch (error) {
      console.error('Test query failed:', error);
      setTestResult({ error: error.message });
    }
  };
  
  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    search(searchTerm);
  };
  
  // Handle reinitialization
  const handleReinitialize = async () => {
    try {
      await initialize();
    } catch (error) {
      console.error('Reinitialization failed:', error);
    }
  };
  
  return (
    <div className="p-4 bg-gray-50 border rounded-lg">
      <h2 className="text-xl font-bold mb-4">RAG System Debugger</h2>
      
      {/* Status */}
      <div className="mb-4">
        <h3 className="font-bold">Status:</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>Initialized:</div>
          <div className={initialized ? "text-green-600" : "text-red-600"}>
            {initialized ? "Yes" : "No"}
          </div>
          
          <div>Loading:</div>
          <div>{loading ? "Yes" : "No"}</div>
          
          <div>Error:</div>
          <div className="text-red-600">{error || "None"}</div>
          
          <div>Knowledge Units:</div>
          <div>{debug?.store?.knowledgeUnitsCount || 0}</div>
          
          <div>API Key Set:</div>
          <div>{debug?.store?.hasApiKey ? "Yes" : "No"}</div>
          
          <div>Last Loaded:</div>
          <div>{debug?.store?.loadTimestamp ? new Date(debug.store.loadTimestamp).toLocaleString() : "Never"}</div>
          
          <div>Load Error:</div>
          <div className="text-red-600">{debug?.store?.loadError || "None"}</div>
          
          <div>AI Integration:</div>
          <div className={debug?.aiIntegration?.status === 'Available' ? "text-green-600" : "text-red-600"}>
            {debug?.aiIntegration?.status || "Unknown"}
          </div>
          
          <div>Last Query:</div>
          <div>{debug?.state?.lastQuery || "None"}</div>
          
          <div>Last Query Type:</div>
          <div>{debug?.state?.lastQueryType || "None"}</div>
          
          <div>Last Response Size:</div>
          <div>{debug?.aiIntegration?.lastResponseLength || 0} chars</div>
          
          <div>Last Confidence:</div>
          <div>{debug?.aiIntegration?.lastConfidence || 0}</div>
        </div>
        
        <button
          onClick={handleReinitialize}
          className="mt-2 px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        >
          Reinitialize RAG
        </button>
      </div>
      
      {/* Test Query */}
      <div className="mb-4">
        <h3 className="font-bold mb-2">Test Query:</h3>
        <form onSubmit={handleTestQuery} className="space-y-2">
          <div>
            <select 
              value={queryType} 
              onChange={(e) => setQueryType(e.target.value)}
              className="w-full p-2 border rounded-md mb-2"
            >
              {Object.values(QueryType).map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div>
            <textarea
              value={testQuery}
              onChange={(e) => setTestQuery(e.target.value)}
              placeholder="Enter a test query..."
              className="w-full p-2 border rounded-md"
              rows={3}
            />
          </div>
          <button
            type="submit"
            disabled={loading || !initialized}
            className="px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-400"
          >
            {loading ? "Loading..." : "Run Test Query"}
          </button>
        </form>
        
        {testResult && (
          <div className="mt-2 p-2 bg-white border rounded-md">
            <h4 className="font-bold">Result:</h4>
            <div className="mb-2">
              <button 
                onClick={() => setShowRawResponse(!showRawResponse)}
                className="text-xs px-2 py-1 bg-gray-200 rounded hover:bg-gray-300"
              >
                {showRawResponse ? "Show Formatted" : "Show Raw JSON"}
              </button>
            </div>
            
            {showRawResponse ? (
              <pre className="text-xs overflow-auto max-h-40 p-2 bg-gray-100 rounded">
                {JSON.stringify(testResult, null, 2)}
              </pre>
            ) : (
              <div className="text-sm">
                <div className="mb-2">
                  <span className="font-semibold">Sources:</span> {testResult.sources?.length || 0}
                </div>
                <div className="mb-2">
                  <span className="font-semibold">Confidence:</span> {testResult.confidence || 0}
                </div>
                <div className="mb-2">
                  <span className="font-semibold">Answer:</span>
                  <div className="whitespace-pre-wrap bg-gray-50 p-2 rounded border mt-1 text-xs">
                    {testResult.answer}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Search */}
      <div className="mb-4">
        <h3 className="font-bold mb-2">Search Knowledge Base:</h3>
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search term..."
            className="flex-1 p-2 border rounded-md"
          />
          <button
            type="submit"
            disabled={!initialized}
            className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400"
          >
            Search
          </button>
        </form>
        
        {searchResults && searchResults.length > 0 && (
          <div className="mt-2">
            <h4 className="font-bold">Found {searchResults.length} results:</h4>
            <div className="max-h-40 overflow-y-auto">
              {searchResults.map((item, index) => (
                <div key={item.sku_id} className="p-2 border-b text-sm">
                  <div className="font-bold">{item.title}</div>
                  <div className="text-gray-700">{item.description}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Raw Debug */}
      <div>
        <h3 className="font-bold mb-2">Raw Debug Info:</h3>
        <pre className="text-xs overflow-auto max-h-40 p-2 bg-gray-100 rounded">
          {JSON.stringify(debug, null, 2)}
        </pre>
      </div>
    </div>
  );
} 